import { useState, useCallback, useEffect } from 'react';
import { blockApi } from 'farmx-api';

const getBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = (error) => reject(error);
});
/**
 * useCommentList: This functions takes the anomalyId and fetches the comment list
 * for the same.
 * It also returns the loading and error status and function to fetch the comment list.
 *
 * @param {*} anomalyId
 * @returns {list, loading, error, fetchCommentList}
 */
export function useCommentList(anomalyId) {
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState();
  const [error, setError] = useState();

  const fetchCommentList = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const response = await blockApi.getAnomalyComments(anomalyId);
      const { data } = response || {};
      setList(data || []);
      setLoading(false);
    } catch (e) {
      setError(e);
      setLoading(false);
    }
  }, [anomalyId]);

  const addComment = async (details, translate, onSuccess, onFailure) => {
    const { text, image } = details;

    if (!text && !image) {
      onFailure({
        message: translate('Please enter a comment or upload image.'),
      });
      return;
    }
    try {
      const imageData = image ? await getBase64(image) : null;
      await blockApi.createAnomalyComment({
        text,
        image_path: imageData,
        anomaly: anomalyId,
      });
      onSuccess();
      fetchCommentList();
    } catch (e) {
      onFailure({
        message: translate('Error in posting comment'),
      });
    }
  };

  const editComment = async (details, translate, onSuccess, onFailure) => {
    const { text, image, id } = details;
    if (!text && !image) {
      onFailure({
        message: translate('Please enter a comment or upload image.'),
      });
      return;
    }
    try {
      const imageData = image ? await getBase64(image) : null;
      await blockApi.updateAnomalyComment(id, {
        text,
        image_path: imageData,
      });
      onSuccess();
      fetchCommentList();
    } catch {
      onFailure({
        message: translate('Error in updating comment'),
      });
    }
  };

  const deleteComment = async (id, translate, onSuccess, onFailure) => {
    try {
      await blockApi.deleteAnomalyComment(id);
      onSuccess();
      fetchCommentList();
    } catch {
      onFailure({
        message: translate('Error in deleting comment'),
      });
    }
  };

  useEffect(() => {
    fetchCommentList();
  }, [fetchCommentList]);

  return {
    list,
    loading,
    error,
    fetchCommentList,
    addComment,
    editComment,
    deleteComment,
  };
}
